/**
 * Get comprehensive website analytics metrics for a specified project
 * @param {Array} data - The CSV data array
 * @param {string} projectName - Name of the project to analyze (e.g. "Bapuji Dental College")
 * @returns {Object} Organized analytics data object
 */
export default function getWebsiteAnalytics(data, projectName) {
    // Initialize the analytics object
    const analytics = {
      dateRange: "",
      sessions: {
        total: 0,
        bot: 0,
        newUsers: 0,
        returningUsers: 0,
        byIntent: [],
        byDevice: [],
        byCountry: [],
        byBrowser: [],
        byOS: [],
        byChannel: [],
        bySource: []
      },
      users: {
        unique: 0,
        topUsers: []
      },
      pages: {
        averagePerSession: 0,
        top: [],
        topEntry: [],
        topExit: []
      },
      engagement: {
        scrollDepth: 0,
        activeTime: 0,
        totalTime: 0,
        insights: []
      },
      performance: {
        score: 0,
        LCP: "",
        INP: "",
        CLS: "",
        byURL: []
      },
      errors: {
        javascript: []
      }
    };
  
    // Helper function to find data by metric and field
    const findData = (metricName, fieldName = null) => {
      return data.find(item => {
        if (metricName === "Date range" && item["Project name"] === metricName) {
          return true;
        }
        
        if (fieldName === null) {
          return item["Project name"] === "Metric" && 
                 item[projectName] === metricName;
        } else {
          return item["Bapuji Dental College"] === fieldName && 
                 (item["Project name"] === metricName || item["Project name"] === null);
        }
      });
    };
  
    // Helper function to get all rows after a metric until the next metric
    const getRowsAfterMetric = (metricName) => {
      const metricIndex = data.findIndex(item => 
        item["Project name"] === "Metric" && 
        item[projectName] === metricName
      );
  
      if (metricIndex === -1) return [];
  
      const result = [];
      let currentIndex = metricIndex + 1;
  
      while (
        currentIndex < data.length && 
        (data[currentIndex]["Project name"] !== "Metric" || !data[currentIndex][projectName])
      ) {
        if (data[currentIndex][projectName] !== null) {
          result.push(data[currentIndex]);
        }
        currentIndex++;
      }
  
      return result;
    };
  
    // Get date range
    const dateRangeRow = data.find(item => item["Project name"] === "Date range");
    if (dateRangeRow) {
      analytics.dateRange = dateRangeRow[projectName];
    }
  
    // Get session counts
    const totalSessions = data.find(item => item["Bapuji Dental College"] === "Total sessions");
    const botSessions = data.find(item => item["Bapuji Dental College"] === "Bot sessions");
    const newUserSessions = data.find(item => item["Bapuji Dental College"] === "Sessions with new users");
    const returningUserSessions = data.find(item => item["Bapuji Dental College"] === "Sessions with returning users");
  
    if (totalSessions && totalSessions.__parsed_extra) {
      analytics.sessions.total = totalSessions.__parsed_extra[0];
    }
    
    if (botSessions && botSessions.__parsed_extra) {
      analytics.sessions.bot = botSessions.__parsed_extra[0];
    }
  
    if (newUserSessions && newUserSessions.__parsed_extra) {
      analytics.sessions.newUsers = newUserSessions.__parsed_extra[0];
    }
  
    if (returningUserSessions && returningUserSessions.__parsed_extra) {
      analytics.sessions.returningUsers = returningUserSessions.__parsed_extra[0];
    }
  
    // Get unique users
    const uniqueUsers = data.find(item => item["Bapuji Dental College"] === "Unique users");
    if (uniqueUsers && uniqueUsers.__parsed_extra) {
      analytics.users.unique = uniqueUsers.__parsed_extra[0];
    }
  
    // Get top users
    const userIdHeader = data.find(item => item["Bapuji Dental College"] === "User id");
    if (userIdHeader) {
      const userIdIndex = data.indexOf(userIdHeader);
      let i = userIdIndex + 1;
      while (i < data.length && data[i]["Project name"] === null && data[i]["Bapuji Dental College"] !== null) {
        if (data[i].__parsed_extra) {
          analytics.users.topUsers.push({
            id: data[i]["Bapuji Dental College"],
            sessions: data[i].__parsed_extra[0],
            country: data[i].__parsed_extra[1],
            device: data[i].__parsed_extra[2]
          });
        }
        i++;
      }
    }
  
    // Get pages per session
    const pagesPerSessionAvg = data.find(item => 
      item["Project name"] === null && 
      item["Bapuji Dental College"] === "Average" && 
      data[data.indexOf(item) - 1]["Bapuji Dental College"] === "Pages per session"
    );
    
    if (pagesPerSessionAvg && pagesPerSessionAvg.__parsed_extra) {
      analytics.pages.averagePerSession = pagesPerSessionAvg.__parsed_extra[0];
    }
  
    // Get scroll depth
    const scrollDepthAvg = data.find(item => 
      item["Project name"] === null && 
      item["Bapuji Dental College"] === "Average" && 
      data[data.indexOf(item) - 1]["Bapuji Dental College"] === "Scroll depth"
    );
    
    if (scrollDepthAvg && scrollDepthAvg.__parsed_extra) {
      analytics.engagement.scrollDepth = scrollDepthAvg.__parsed_extra[0];
    }
  
    // Get time spent
    const activeTime = data.find(item => item["Bapuji Dental College"] === "Active time");
    const totalTime = data.find(item => item["Bapuji Dental College"] === "Total time");
    
    if (activeTime && activeTime.__parsed_extra) {
      analytics.engagement.activeTime = activeTime.__parsed_extra[0];
    }
    
    if (totalTime && totalTime.__parsed_extra) {
      analytics.engagement.totalTime = totalTime.__parsed_extra[0];
    }
  
    // Get user intent
    const userIntentRows = getRowsAfterMetric("User intent");
    userIntentRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 2) {
        analytics.sessions.byIntent.push({
          intent: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0],
          percentage: row.__parsed_extra[1]
        });
      }
    });
  
    // Get devices
    const deviceRows = getRowsAfterMetric("Devices");
    deviceRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 2) {
        analytics.sessions.byDevice.push({
          device: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0],
          percentage: row.__parsed_extra[1]
        });
      }
    });
  
    // Get countries
    const countryRows = getRowsAfterMetric("Countries");
    countryRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 2) {
        analytics.sessions.byCountry.push({
          country: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0],
          percentage: row.__parsed_extra[1]
        });
      }
    });
  
    // Get browsers
    const browserRows = getRowsAfterMetric("Browsers");
    browserRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 2) {
        analytics.sessions.byBrowser.push({
          browser: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0],
          percentage: row.__parsed_extra[1]
        });
      }
    });
  
    // Get operating systems
    const osRows = getRowsAfterMetric("Operating systems");
    osRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 2) {
        analytics.sessions.byOS.push({
          os: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0],
          percentage: row.__parsed_extra[1]
        });
      }
    });
  
    // Get channels
    const channelRows = getRowsAfterMetric("Channel");
    channelRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 1) {
        analytics.sessions.byChannel.push({
          channel: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0]
        });
      }
    });
  
    // Get sources
    const sourceRows = getRowsAfterMetric("Source");
    sourceRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 1) {
        analytics.sessions.bySource.push({
          source: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0]
        });
      }
    });
  
    // Get top pages
    const topPagesRows = getRowsAfterMetric("Top pages");
    topPagesRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 1) {
        analytics.pages.top.push({
          url: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0]
        });
      }
    });
  
    // Get top entry pages
    const topEntryRows = getRowsAfterMetric("Top entry pages");
    topEntryRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 1) {
        analytics.pages.topEntry.push({
          url: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0]
        });
      }
    });
  
    // Get top exit pages
    const topExitRows = getRowsAfterMetric("Top exit pages");
    topExitRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 1) {
        analytics.pages.topExit.push({
          url: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0]
        });
      }
    });
  
    // Get insights
    const insightsRows = getRowsAfterMetric("Insights");
    insightsRows.forEach(row => {
      if (row.__parsed_extra && row.__parsed_extra.length >= 2) {
        analytics.engagement.insights.push({
          insight: row["Bapuji Dental College"],
          sessions: row.__parsed_extra[0],
          percentage: row.__parsed_extra[1]
        });
      }
    });
  
    // Get performance data
    const performanceScore = data.find(item => 
      item["Project name"] === null && 
      item["Bapuji Dental College"] === "Score" && 
      data[data.indexOf(item) - 1]["Bapuji Dental College"] === "Performance overview"
    );
    
    const LCP = data.find(item => item["Bapuji Dental College"] === "LCP (Largest Contentful Paint)");
    const INP = data.find(item => item["Bapuji Dental College"] === "INP (Interaction to Next Paint)");
    const CLS = data.find(item => item["Bapuji Dental College"] === "CLS (Cumulative Layout Shift)");
    
    if (performanceScore && performanceScore.__parsed_extra) {
      analytics.performance.score = performanceScore.__parsed_extra[0];
    }
    
    if (LCP && LCP.__parsed_extra) {
      analytics.performance.LCP = LCP.__parsed_extra[0];
    }
    
    if (INP && INP.__parsed_extra) {
      analytics.performance.INP = INP.__parsed_extra[0];
    }
    
    if (CLS && CLS.__parsed_extra) {
      analytics.performance.CLS = CLS.__parsed_extra[0];
    }
  
    // Get URL performance
    const urlPerformanceHeader = data.find(item => 
      item["Project name"] === null && 
      item["Bapuji Dental College"] === "URL" && 
      data[data.indexOf(item) - 1]["Bapuji Dental College"] === "URL performance"
    );
    
    if (urlPerformanceHeader) {
      const headerIndex = data.indexOf(urlPerformanceHeader);
      let i = headerIndex + 1;
      while (i < data.length && data[i]["Project name"] === null && data[i]["Bapuji Dental College"] !== null && data[i]["Bapuji Dental College"] !== "Metric") {
        if (data[i].__parsed_extra && data[i].__parsed_extra.length >= 4) {
          analytics.performance.byURL.push({
            url: data[i]["Bapuji Dental College"],
            score: data[i].__parsed_extra[0],
            LCP: data[i].__parsed_extra[1],
            INP: data[i].__parsed_extra[2],
            CLS: data[i].__parsed_extra[3]
          });
        }
        i++;
      }
    }
  
    // Get JavaScript errors
    const jsErrorsHeader = data.find(item => 
      item["Project name"] === null && 
      item["Bapuji Dental College"] === null && 
      item.__parsed_extra && 
      item.__parsed_extra[0] === "No. of sessions" && 
      item.__parsed_extra[1] === "% of sessions" &&
      data[data.indexOf(item) - 1]["Bapuji Dental College"] === " total JavaScript errors"
    );
    
    if (jsErrorsHeader) {
      const headerIndex = data.indexOf(jsErrorsHeader);
      let i = headerIndex + 1;
      while (i < data.length && data[i]["Project name"] === null) {
        if (data[i].__parsed_extra && data[i].__parsed_extra.length >= 2) {
          analytics.errors.javascript.push({
            error: data[i]["Bapuji Dental College"],
            sessions: data[i].__parsed_extra[0],
            percentage: data[i].__parsed_extra[1]
          });
        }
        i++;
      }
    }
  
    return analytics;
  }
  
  // Example usage:
  // const analytics = getWebsiteAnalytics(csvData, "Bapuji Dental College");
  // console.log(analytics);